/**
 * An AJAX Form is a form that is to be processed via AJAX. It does validation checking if HTML5 validation
 * isn't available, and submitting. It is meant to be used on a form element itself, but can be attached to
 * any element using properties.
 *
 * If you specify the element on a form, then it will use the elements ACTION and METHOD parameters as default.
 * If the AJAX endpoint is different from the regular endpoint, then using the endpoint attribute and
 * method can be specified and they will be used instead.
 *
 * Form validation checks usual HTML5 field validation. Right now it only checks two, required and minlength.
 * Extend the FIELD_VALIDATORS object if more are required (such as with pattern).
 *
 * There are some default error messages specified. If your form field requires a specific error message, you
 * can specify it on the form field itself with data-error-message-{type} (for instance,
 * data-error-message-required="You must supply a username.".
 * @module components/ajax-form
 **/
import _ from "underscore";
import forms from "web/script/dom/forms";
import factory from "web/script/dom/factory";
import traversal from "web/script/dom/traversal";
import baustein from "baustein";
import userProfiler from "web/script/modules/userprofiler";
import requester from "web/script/modules/requester";


var VALID_METHODS = ["get", "post", "delete", "put"];

var FIELD_VALIDATOR_ERROR_MESSAGES = {
    required: "This field is required.",
    minlength: "This must be at least %s characters long.",
    maxlength: "This must be less than %s characters long.",
};

var FIELD_VALIDATORS = {
    required: function(field, value) {
        let element = Array.isArray(field) ? field[0] : field;
        let isRequired = element.required === true;
        return {
            valid: !isRequired || !(isRequired && (!value || value.length === 0)),
        };
    },
    minlength: function(field, value) {
        let element = Array.isArray(field) ? field[0] : field;
        let hasMinlength = element.minLength > 0;
        let minlength = parseInt(element.minLength, 10);
        return {
            valid: !hasMinlength || (hasMinlength && value.length >= minlength),
            requirement: minlength,
        };
    },
    maxlength: function(field, value) {
        let element = Array.isArray(field) ? field[0] : field;
        let hasMaxlength = element.maxLength > 0;
        let maxlength = parseInt(element.maxLength, 10);
        return {
            valid: !hasMaxlength || (hasMaxlength && value.length <= maxlength),
            requirement: maxlength,
        };
    },
};

/**
 * An AJAX Form is a form that has the ability to be sent via a POST
 * request. It should be attached to a form.
 * @constructor
 * @alias module:components/ajax-form
 * @extends module:baustein
 */
export default baustein.register(
    "ajax-form",
    /** @lends module:components/ajax-form.prototype */
    {
        _submitButton: null,

        _shouldSubmit: false,

        _isSendingData: false,

        defaultOptions: {
            endpoint: null,
            method: null,
            loginRequired: false,
        },

        setupEvents: function(add) {
            add("submit", this.submitForm);
        },

        init: function() {
            if (this.el.tagName.toLowerCase() === "form") {
                // get the endpoint and method from the form, if not provided
                if (!_.isString(this.options.endpoint)) {
                    this.options.endpoint = this.el.action;
                }

                // If an input is present with the name "http_method" use its value
                // instead of the form's method attribute.
                var methodInput = this.el.querySelector('[name="http_method"]');
                if (methodInput && methodInput.value) {
                    this.options.method = methodInput.value;
                }
            }

            this.options.method = (this.options.method || "").toLowerCase();
            if (!_.contains(VALID_METHODS, this.options.method)) {
                this.options.method = VALID_METHODS[0];
            }

            this._onSubmitSuccess = this._onSubmitSuccess.bind(this);
            this._onSubmitFailure = this._onSubmitFailure.bind(this);

            return this;
        },

        /**
         * Update the form with the fields and values provided.
         * @param {Object} data - a dictionary of form field names with their values
         */
        updateForm: function(data) {
            data = data || {};

            _.each(data, (item, name) => {
                if (name === "csrfmiddlewaretoken") {
                    // don't refill this
                    return;
                }

                let fields = Array.from(this.el.querySelectorAll(`[name=${name}]`));
                if (fields.length === 1) {
                    fields[0].value = item.value;
                } else if (fields.length > 1) {
                    let filteredFields = _.filter(fields, (field) => {
                        return field.value === item.value;
                    });

                    _.each(filteredFields, (field) => {
                        field.checked = true;
                    });
                }
            });
        },

        /**
         * Perform any validation steps, and if succeeded, submit the form.
         * @param {Event} [e] - the submit event to override
         */
        submitForm: function(e) {
            if (e) {
                e.preventDefault();
            }

            var validity = this._checkFormValid();
            if (!validity.valid) {
                return this._onValidationFailure(validity.fields);
            }

            if (!this.options.endpoint) {
                throw Error(
                    'No end point to go to! Specify action (if form) or "post-location" option'
                );
            }

            var data = forms.serialize(this.el);

            if (!userProfiler.isLoggedIn() && this.options.loginRequired) {
                this.emit("login-required", { formData: data });
            } else {
                this._sendData(data, validity.fields);
            }
        },

        _sendData: function(data, validityData) {
            if (this._isSendingData) {
                return;
            }
            this._isSendingData = true;
            this._onBeforeSubmit(validityData);

            var options = {
                method: this.options.method,
            };

            if (this.options.method === "get") {
                options.query = data;
            } else {
                options.body = data;
            }

            requester(this.options.endpoint, options)
                .then(function(response) {
                    if (response.headers.get("Content-Type") === "application/json") {
                        return response.json();
                    } else {
                        return response.text();
                    }
                })
                .then(this._onSubmitSuccess)
                .catch(this._onSubmitFailure);
        },

        _checkFormValid: function() {
            var requiredFields = Array.from(this.el.querySelectorAll("input, select, textarea"));

            var checkedFields = {};

            for (var i = 0; i < requiredFields.length; i++) {
                var field = requiredFields[i];
                var type = field.getAttribute("type");
                var name = field.getAttribute("name");

                if (checkedFields[name]) {
                    continue;
                }

                var value = field.value;

                if (type === "radio" || type === "checkbox") {
                    // field should be the selected one, if available
                    var checked = Array.from(
                        this.el.querySelectorAll(`[name=${field.getAttribute("name")}]:checked`)
                    );

                    value = checked.length > 0 ? checked[0].value : null;

                    field = checked.length === 1 ? checked : field;
                }

                var validationResults = _.reduce(
                    FIELD_VALIDATORS,
                    _.partial(this._fieldValidatorIterator, field, value),
                    []
                );

                if (validationResults.length > 0) {
                    // we have errors :(
                    checkedFields[name] = {
                        valid: false,
                        error: validationResults.join(" "),
                    };
                } else {
                    checkedFields[name] = { valid: true, value: value };
                }
            }

            return {
                valid: _.every(checkedFields, function(field) {
                    return field.valid === true;
                }),
                fields: checkedFields,
            };
        },

        _fieldValidatorIterator: function(field, value, memo, fn, name) {
            var result = fn.call(this, field, value);
            if (!result.valid) {
                var fieldMessage = field.data("error-message-" + name);
                var error = fieldMessage ? fieldMessage : FIELD_VALIDATOR_ERROR_MESSAGES[name];
                if (result.requirement) {
                    error = error.replace("%s", result.requirement);
                }
                memo.push(error);
            }
            return memo;
        },

        _showFieldErrors: function(data) {
            _.each(
                data,
                function(item, name) {
                    var fieldContainer = traversal.closest(
                        this.el.querySelector(`[name=${name}]`),
                        ".form-item"
                    );
                    var fieldContainerSibling = fieldContainer.nextElementSibling;

                    if (item.valid) {
                        if (fieldContainerSibling.classList.contains("form-error")) {
                            fieldContainerSibling.parentNode.removeChild(fieldContainerSibling);
                        }
                        return;
                    }

                    if (fieldContainerSibling.classList.contains("form-error")) {
                        fieldContainerSibling.textContent = item.error;
                    } else {
                        let errorEl = factory.fromString('<div class="form-error"></div>');
                        let parent = fieldContainer.parentElement;
                        errorEl.textContent = item.error;
                        parent.insertBefore(errorEl, fieldContainer.nextElementSibling);
                    }
                }.bind(this)
            );
        },

        _onBeforeSubmit: function(data) {
            this.emit("form-before-submit", { formData: data });
        },

        _onSubmitSuccess: function(data) {
            this._isSendingData = false;
            this.emit("form-submit-success", { formData: data });
        },

        _onSubmitFailure: function(data) {
            this._isSendingData = false;
            this.emit("form-error", { formData: data });
        },

        _onValidationFailure: function(data) {
            this._showFieldErrors(data);
            this.emit("form-invalid", { formData: data });
        },
    }
);
